import React, { FC, useMemo } from 'react';
import { ProductFilter, ProductMetaData } from '../../../../models/Product';
import { getLoanFormats, LoanFormat, mapLoanFormat } from '../../../../models/LoanFormat';
import { FilterSetting, ProductSpec } from '../../../store/api';
import { BackIssues, CatalogueProductGroup, LanguageLearningProductGroup, LoteProductGroup } from '../ProductGroup';
import RoutingUtils from '../../utils/utils-routing';
import Utils from '../../utils';
import { PRODUCT_CLASS_LOTE, PRODUCT_CLASS_STANDARD } from '../../../store/constants';
import { LOANFORMAT_EPRESS_OBJECT } from '../../../store/loanFormat/actions';
import { useTranslation } from 'react-i18next';
import { getGenreId } from '../../common/translationMappers/genreIdMapper';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../../utils/togglz';
import MetadataUtils from '../../utils/utils-metadata';

type Props = {
    readonly product: ProductMetaData;
};

export const ProductDetailProductGroups: FC<Props> = ({ product }) => {
    const productLoanFormat = mapLoanFormat(product.loanFormat)!;
    const otherLoanFormats = getLoanFormats().filter(loanFormat => loanFormat !== productLoanFormat);

    return (
        <>
            {productLoanFormat === LOANFORMAT_EPRESS_OBJECT.name ? <BackIssues product={product} /> : null}
            <SeriesProductGroup loanFormat={productLoanFormat} product={product} dataSource={'catalogue'} />
            <AuthorProductGroup loanFormat={productLoanFormat} product={product} dataSource={'catalogue'} />
            <GenreProductGroup loanFormat={productLoanFormat} product={product} dataSource={'catalogue'} />

            {otherLoanFormats.map(loanFormat => (
                <React.Fragment key={loanFormat}>
                    <SeriesProductGroup loanFormat={loanFormat} product={product} dataSource={'catalogue'} />
                    <AuthorProductGroup loanFormat={loanFormat} product={product} dataSource={'catalogue'} />
                    <GenreProductGroup loanFormat={loanFormat} product={product} dataSource={'catalogue'} />
                </React.Fragment>
            ))}
        </>
    );
};

export const ForeignLanguageProductDetailProductGroups: FC<Props> = ({ product }) => {
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const productLoanFormat = mapLoanFormat(product.loanFormat)!;
    const otherLoanFormats = getLoanFormats().filter(loanFormat => loanFormat !== productLoanFormat);
    const dataSource =
        product.productClass === PRODUCT_CLASS_LOTE
            ? isMultiLanguageShopEnabled
                ? 'catalogue'
                : 'lote'
            : 'languagelearning';

    return (
        <>
            {productLoanFormat === LOANFORMAT_EPRESS_OBJECT.name ? <BackIssues product={product} /> : null}

            <SeriesProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={product.language}
                dataSource={dataSource}
            />
            <AuthorProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={product.language}
                dataSource={dataSource}
            />
            <GenreProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={product.language}
                dataSource={dataSource}
            />

            <SeriesProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={'Other languages'}
                dataSource={dataSource}
            />
            <AuthorProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={'Other languages'}
                dataSource={dataSource}
            />
            <GenreProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={'Other languages'}
                dataSource={dataSource}
            />

            {otherLoanFormats.map(loanFormat => (
                <React.Fragment key={loanFormat}>
                    <SeriesProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={product.language}
                        dataSource={dataSource}
                    />
                    <AuthorProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={product.language}
                        dataSource={dataSource}
                    />
                    <GenreProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={product.language}
                        dataSource={dataSource}
                    />
                    <SeriesProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={'Other languages'}
                        dataSource={dataSource}
                    />
                    <AuthorProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={'Other languages'}
                        dataSource={dataSource}
                    />
                    <GenreProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={'Other languages'}
                        dataSource={dataSource}
                    />
                </React.Fragment>
            ))}
        </>
    );
};

type SeriesProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language?: string;
    readonly dataSource: 'catalogue' | 'lote' | 'languagelearning';
};
const SeriesProductGroup: FC<SeriesProductGroupProps> = ({ loanFormat, product, language, dataSource }) => {
    const productLoanFormat = mapLoanFormat(product.loanFormat)!;
    const languageFilter = useMemo(() => buildLanguageFilter(product, language), [product, language]);
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);

    const productSpec: ProductSpec = useMemo<ProductSpec>(
        () => ({
            loanFormat,
            filters: {
                series: { include: [product.seriesName!] },
                productId: { exclude: [product.id] },
                ageCategory: product.ageCategory ? { include: [product.ageCategory] } : {},
                ...languageFilter
            },
            ageCategory: product.ageCategory
        }),
        [product, languageFilter, loanFormat]
    );

    const seeAllUrl = buildSeeAllUrl({
        product,
        loanFormat,
        filter: productSpec.filters!,
        isMultiLanguageShopEnabled
    });

    const titlePrefix = loanFormat === productLoanFormat ? t('components.productDetails.more') : '';
    const moreFromContext = language ? 'language' : 'product';

    const title = t(`components.productDetails.moreFrom_${moreFromContext}`, {
        titlePrefix,
        loanFormat: t(`components.productDetails.${loanFormat}`),
        language: t(`filters:language.${language?.toLowerCase()}`, {
            defaultValue: t('otherLanguages')
        }),
        type: product.seriesName
    });

    if (!product.seriesName) {
        return null;
    }

    switch (dataSource) {
        case 'lote':
            return <LoteProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title.trim()} />;
        case 'languagelearning':
            return (
                <LanguageLearningProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title.trim()} />
            );
        default:
            return (
                <CatalogueProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title.trim()}
                    shopLanguage={undefined}
                />
            );
    }
};

type AuthorProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language?: string;
    readonly dataSource: 'catalogue' | 'lote' | 'languagelearning';
};
const AuthorProductGroup: FC<AuthorProductGroupProps> = ({ loanFormat, product, language, dataSource }) => {
    const productLoanFormat = mapLoanFormat(product.loanFormat)!;
    const languageFilter = useMemo(() => buildLanguageFilter(product, language), [product, language]);
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);

    const authors = useMemo(() => (product.authors ? product.authors.filter(a => !!a.id) : []), [product.authors]);

    const authorNames = authors.map(a => MetadataUtils.getPerformerFullname(a)).join(', ');
    const authorIds = useMemo(() => authors.map(a => a.id!), [authors]);

    const productSpec = useMemo<ProductSpec>(
        () => ({
            loanFormat,
            filters: {
                author: { include: authorIds },
                productId: { exclude: [product.id] },
                ageCategory: product.ageCategory ? { include: [product.ageCategory] } : {},
                ...languageFilter
            },
            ageCategory: product.ageCategory
        }),
        [product, authorIds, languageFilter, loanFormat]
    );

    const seeAllUrl = buildSeeAllUrl({
        product,
        loanFormat,
        filter: productSpec.filters!,
        isMultiLanguageShopEnabled
    });

    const titlePrefix = loanFormat === productLoanFormat ? t('components.productDetails.more') : '';
    const moreFromContext = language ? 'language' : 'product';

    const title = t(`components.productDetails.moreFrom_${moreFromContext}`, {
        titlePrefix,
        loanFormat: t(`components.productDetails.${loanFormat}`),
        language: t(`filters:language.${language?.toLowerCase()}`, {
            defaultValue: t('otherLanguages')
        }),
        type: authorNames,
        context: language ? 'language' : 'product'
    });

    if (!product.authors || product.authors.length === 0) {
        return null;
    }

    switch (dataSource) {
        case 'lote':
            return <LoteProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title.trim()} />;
        case 'languagelearning':
            return (
                <LanguageLearningProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title.trim()} />
            );
        default:
            return (
                <CatalogueProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title.trim()}
                    shopLanguage={undefined}
                />
            );
    }
};

type GenreProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language?: string;
    readonly dataSource: 'catalogue' | 'lote' | 'languagelearning';
};
const GenreProductGroup: FC<GenreProductGroupProps> = ({ loanFormat, product, language, dataSource }) => {
    const productLoanFormat = mapLoanFormat(product.loanFormat)!;
    const languageFilter = useMemo(() => buildLanguageFilter(product, language), [product, language]);
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);

    const genreIds = useMemo(
        () => (product.genres ? product.genres.map(g => (g.subGenres ? g.subGenres[0].id : g.id)) : []),
        [product.genres]
    );
    const localisedGenreIds = useMemo(
        () =>
            product.genres
                ? product.genres
                      .map(g =>
                          g.subGenres
                              ? t(`filters:genre.${getGenreId(g.subGenres[0].id)}`)
                              : t(`filters:genre.${getGenreId(g.id)}`)
                      )
                      .join(', ')
                : [],
        [product.genres, t]
    );

    const productSpec: ProductSpec = useMemo(
        () => ({
            loanFormat,
            filters: {
                genre: { include: genreIds },
                productId: { exclude: [product.id] },
                ageCategory: product.ageCategory ? { include: [product.ageCategory] } : {},
                ...languageFilter
            },
            ageCategory: product.ageCategory
        }),
        [loanFormat, genreIds, product, languageFilter]
    );

    const seeAllUrl = buildSeeAllUrl({
        product,
        loanFormat,
        filter: productSpec.filters!,
        isMultiLanguageShopEnabled
    });

    const titlePrefix = loanFormat === productLoanFormat ? t('components.productDetails.more') : '';
    const moreFromContext = language ? 'language' : 'product';

    const title = t(`components.productDetails.moreFrom_${moreFromContext}`, {
        titlePrefix,
        loanFormat: t(`components.productDetails.${loanFormat}`),
        language: t(`filters:language.${language?.toLowerCase()}`, {
            defaultValue: t('otherLanguages')
        }),
        type: localisedGenreIds,
        context: language ? 'language' : 'product'
    });

    if (!product.genres || product.genres.length === 0) {
        return null;
    }

    switch (dataSource) {
        case 'lote':
            return <LoteProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title.trim()} />;
        case 'languagelearning':
            return (
                <LanguageLearningProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title.trim()} />
            );
        default:
            return (
                <CatalogueProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title.trim()}
                    shopLanguage={undefined}
                />
            );
    }
};

interface BuildSeeAllUrlProps {
    readonly product: ProductMetaData;
    readonly loanFormat: LoanFormat;
    readonly filter: { [key: string]: FilterSetting };
    isMultiLanguageShopEnabled: boolean;
}

const buildSeeAllUrl = ({ product, loanFormat, filter, isMultiLanguageShopEnabled }: BuildSeeAllUrlProps): string => {
    const targetLanguage = 'all-languages';
    const mappedProductClass =
        isMultiLanguageShopEnabled && product.productClass === PRODUCT_CLASS_LOTE
            ? PRODUCT_CLASS_STANDARD
            : product.productClass;

    switch (mappedProductClass) {
        case 'STANDARD':
            return Utils.catalogueLocationDescriptorString({
                loanFormat,
                filter,
                shopLanguage: undefined,
                isMultiLanguageShopEnabled,
                ageCategory: undefined
            });
        case 'LOTE':
            return RoutingUtils.foreignLanguageDescriptorString({
                language: targetLanguage,
                loanFormat,
                filter,
                ageCategory: undefined
            });
        case 'LANGUAGE_LEARNING':
            return RoutingUtils.languageLearningDescriptorString({
                language: targetLanguage,
                loanFormat,
                filter,
                shopLanguage: undefined,
                isMultiLanguageShopEnabled,
                ageCategory: undefined
            });
        case 'CAMPAIGN':
            return Utils.catalogueLocationDescriptorString({
                loanFormat,
                filter,
                shopLanguage: undefined,
                isMultiLanguageShopEnabled,
                ageCategory: undefined
            });
        case 'LOCAL':
            return '';
    }
};

const buildLanguageFilter = (product: ProductMetaData, language?: string): ProductFilter | undefined => {
    if (!language) return undefined;
    if (!product.language) return undefined;

    if (product.language === language) {
        return { language: { include: [product.language] } };
    }

    return { language: { exclude: [product.language] } };
};
